<template>
  <div class="auction-table">
    <Toast ref="toast" />

    <DataTable
      :value="tableData"
      dataKey="id"
      responsiveLayout="scroll"
      sortField="score"
      :sortOrder="-1"
      emptyMessage="Подходящих стратегий сейчас нет"
    >
      <!--      <Column expander style="width: 3rem" />-->

      <!-- ================= АРТЕФАКТ ================= -->
      <Column field="name" header="Артефакт">
        <template #body="{ data }">
          <div
            :class="{
              'row-highlight': highlightMap[data.id]
            }"
            style="white-space: nowrap; position: relative"
          >
            <span @click="copyToClipboard(data.name, 'name')" v-text="data.name"></span>&nbsp;
            <span style="white-space: nowrap" :style="`color: ${getColorQlt(data.qlt)}`">
              {{ data.additionalInfo?.ptn ?? data.ptn }}
            </span>

            <span v-if="highlightMap[data.id]" class="click-indicator">
              👤 {{ highlightMap[data.id].adminId }}
            </span>
          </div>
        </template>
      </Column>

      <Column field="score" header="Score" sortable>
        <template #body="{ data }">
          <span v-tooltip.bottom="artefactScoreTooltip(data.scoreDetails)">
            {{ data.score.toFixed(1) }}
          </span>
        </template>
      </Column>

      <Column field="averagePrice" header="Медиана">
        <template #body="{ data }">
          {{ formatPrice(Number(data.averagePrice)) }}
        </template>
      </Column>

      <!--      <Column field="salePrice" header="Цена продажи">-->
      <!--        <template #body="{ data }">-->
      <!--          <div-->
      <!--            :class="{-->
      <!--              'row-highlight': highlightMapPrice[data.id]-->
      <!--            }"-->
      <!--            style="white-space: nowrap; position: relative"-->
      <!--          >-->
      <!--            <span @click="copyToClipboard(data.salePrice, 'price')">{{-->
      <!--              formatPrice(Number(data.salePrice))-->
      <!--            }}</span>-->

      <!--            <span v-if="highlightMapPrice[data.id]" class="click-indicator">-->
      <!--              👤 {{ highlightMapPrice[data.id].adminId }}-->
      <!--            </span>-->
      <!--          </div>-->
      <!--        </template>-->
      <!--      </Column>-->

      <Column field="currentElems" header="Лоты">
        <template #body="{ data }">
          <div>
            <span v-for="(lot, index) in data.currentElems" :key="index">
              <span>{{ formatPrice(lot.price) }}</span>

              <template v-if="lot.additionalInfo?.ptn !== undefined">
                - <span>{{ lot.additionalInfo.ptn }}</span> </template
              >;
            </span>
          </div>
        </template>
      </Column>

      <Column field="currentElems" header="Время">
        <template #body="{ data }">
          <div v-if="data.currentElems.length">
            <div style="white-space: nowrap">
              {{ formatMs(getAuctionTimer(data.startTime, data.endTime).passedMs) }}
            </div>

            <div style="font-size: 12px; white-space: nowrap">
              {{ formatMs(getAuctionTimer(data.startTime, data.endTime).leftMs) }}
            </div>
          </div>
        </template>
      </Column>

      <Column header="Продажи">
        <template #body="{ data }">
          <div>{{ data.sales24h }} / 24ч</div>
          <div style="font-size: 12px">{{ data.sales48h }} / 48ч</div>
        </template>
      </Column>

      <Column field="historySalePrices" header="Количество проданных">
        <template #body="{ data }">
          {{ data.historyCount ?? data.historySalePrices.length }}
        </template>
      </Column>

      <Column header="Стратегии">
        <template #body="{ data }">
          <div class="strategies-container">
            <div
              v-for="(strategy, index) in data.multiLotStrategies"
              :key="index"
              class="strategy-card"
            >
              <div
                class="strategy-header"
                @click="copyToClipboard(strategy.salePricePerLot.toFixed(), 'price')"
              >
                <strong>{{ strategy.lotsCount }} шт.</strong>
                <span :style="`color: ${strategy.totalProfit > 0 ? 'green' : 'red'}`">
                  {{ formatPrice(strategy.totalProfit) }}
                </span>
              </div>
              <div
                class="strategy-details"
                @click="copyToClipboard(strategy.salePricePerLot.toFixed(), 'price')"
              >
                <div>Стоимость выкупа: {{ formatPrice(strategy.totalCost) }}</div>
                <div v-tooltip.bottom="artefactScoreTooltip(strategy.scoreDetails)">
                  Score: {{ strategy.score.toFixed(1) }}
                </div>
                <div>Продажа: {{ formatPrice(strategy.totalSalePrice) }}</div>
                <!--                <div>Комиссия: {{ formatPrice(strategy.totalCommission) }}</div>-->
                <div>Сред. цена лота: {{ formatPrice(strategy.averageLotPrice) }}</div>
                <div>Прибыль/лот: {{ formatPrice(strategy.profitPerLot) }}</div>
                <div>Цена продажи: {{ formatPrice(strategy.salePricePerLot.toFixed()) }}</div>
              </div>
              <div
                class="lots-prices"
                @click="copyToClipboard(strategy.salePricePerLot.toFixed(), 'price')"
              >
                <small
                  >Цены лотов: {{ strategy.lots.map(l => formatPrice(l.price)).join(', ') }}</small
                >
              </div>
            </div>
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps, onBeforeUnmount, onMounted, ref, watch, withDefaults } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

import { formatPrice } from '@/common/function'
import { getNameArtFromId } from '@/common/list'
import { useWebSocketStore } from '@/store/websocket.store'
import { copyText } from 'vue3-clipboard'
import Toast from 'primevue/toast'
import {
  ArtefactScoreDetails,
  calculateArtefactScore,
  artefactScoreTooltip
} from '@/common/artefact-score'
import {
  ArtefactFilterSettings,
  DEFAULT_ARTEFACT_FILTER_SETTINGS,
  LiquidityWindow,
  passesMultiLotStrategyFilters
} from '@/common/artefact-filters'

const props = withDefaults(
  defineProps<{
    onlyGoodLots?: boolean
    liquidityWindow?: LiquidityWindow
    minLiquiditySales?: number
    minBuyoutProfit?: number
  }>(),
  {
    onlyGoodLots: DEFAULT_ARTEFACT_FILTER_SETTINGS.onlyLiquid,
    liquidityWindow: DEFAULT_ARTEFACT_FILTER_SETTINGS.liquidityWindow,
    minLiquiditySales: DEFAULT_ARTEFACT_FILTER_SETTINGS.minSales,
    minBuyoutProfit: DEFAULT_ARTEFACT_FILTER_SETTINGS.minProfit
  }
)

const toast = ref()

const wsStore = useWebSocketStore()

const tableData = ref<StrategyTableRow[]>([])

// const expandedRows = ref<Record<string, boolean>>({});

const qltMap: Record<number, string> = {
  0: '#E0E0E0',
  1: '#4CAF50',
  2: '#2196F3',
  3: '#9C27B0',
  4: '#F44336',
  5: '#FFD700'
}

const getColorQlt = (qlt: number | undefined) => qltMap[qlt ?? 0] ?? 'white'

interface CurrentLot {
  price: number
  startTime: string
  endTime: string
  currentPrice: number
  additionalInfo: object | null
}

interface MultiLotStrategy {
  lotsCount: number
  totalCost: number
  totalSalePrice: number
  totalCommission: number
  totalProfit: number
  averageLotPrice: number
  profitPerLot: number
  lots: CurrentLot[]
  salePricePerLot: number
}

export interface TableRow {
  qlt: number
  ptn: string
  averagePrice: string | null
  historyMinPrice: number
  historySalePrices: HistoryLot[]
  currentPrice: number
  currentElems: CurrentLot[]
  profit: string | null
  commission: number | null
  salePrice: number | null
  startPrice: number | null
  startTime: string | null
  endTime: string | null
  isBid?: boolean
  additionalInfo: object | null
  multiLotStrategies: MultiLotStrategy[]
  historyCount?: number
  sales24h: number
  sales48h: number
}

interface StrategyTableRow extends TableRow {
  id: string
  name: string
  score: number
  scoreDetails: ArtefactScoreDetails
  multiLotStrategies: ScoredMultiLotStrategy[]
}

interface ScoredMultiLotStrategy extends MultiLotStrategy {
  score: number
  scoreDetails: ArtefactScoreDetails
}

interface StrategyPayload {
  multiLotStrategies: Record<string, { name?: string; rows?: TableRow[] }>
}

interface HistoryLot {
  price: number
  time: string | Date
  additional: object | null
}

function onArtefactProfitAll(data: StrategyPayload) {
  const next: StrategyTableRow[] = []
  const now = Date.now()

  for (const artefactId in data?.multiLotStrategies ?? {}) {
    const artefact = data.multiLotStrategies[artefactId]
    if (!artefact?.rows) continue

    const name = artefact.name || getNameArtFromId(artefactId)

    artefact.rows.forEach((row: TableRow) => {
      const strategies = (row.multiLotStrategies ?? [])
        .filter(strategy => passesMultiLotStrategyFilters(row, strategy, filterSettings.value, now))
        .map(strategy => {
          const scoreDetails = calculateArtefactScore(
            {
              ...row,
              rawProfit: strategy.totalProfit,
              currentPrice: strategy.totalCost,
              lotsCount: strategy.lotsCount
            },
            props.liquidityWindow,
            now
          )
          return { ...strategy, score: scoreDetails.score, scoreDetails }
        })
        .sort((a, b) => b.score - a.score || b.totalProfit - a.totalProfit)

      if (strategies.length === 0 || row.isBid) return

      const item = {
        ...row,
        multiLotStrategies: strategies,
        score: strategies[0].score,
        scoreDetails: strategies[0].scoreDetails,
        name,
        id: `${artefactId}_${row.qlt}_${row.ptn}`
      }

      next.push(item)
    })
  }

  next.sort((a, b) => {
    return (
      b.score - a.score ||
      b.multiLotStrategies[0].totalProfit - a.multiLotStrategies[0].totalProfit ||
      b.sales48h - a.sales48h ||
      a.id.localeCompare(b.id)
    )
  })

  tableData.value = next
}

const filterSettings = computed<ArtefactFilterSettings>(() => ({
  onlyLiquid: props.onlyGoodLots,
  liquidityWindow: props.liquidityWindow,
  minSales: props.minLiquiditySales,
  minProfit: props.minBuyoutProfit
}))

const copyingNotification = (text: string) => {
  toast.value?.add({
    severity: 'info',
    summary: 'Скопировано',
    detail: text,
    life: 3000
  })
}

const copyToClipboard = (text: string, type: string) => {
  copyingNotification(text)
  copyText(text, undefined, (error: Error) => {
    if (error) console.error('Copy error:', error)
  })

  wsStore.sendArt({
    text,
    type
  })
}

function getAuctionTimer(startIso: string, endIso: string) {
  const start = new Date(startIso).getTime()
  const end = new Date(endIso).getTime()
  const now = Date.now()

  // const duration = end - start;
  const passed = Math.max(0, now - start)
  const left = Math.max(0, end - now)

  return {
    passedMs: passed,
    leftMs: left
  }
}

function formatMs(ms: number): string {
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  const h = Math.floor(m / 60)

  if (h > 0) return `${h}ч ${m % 60}м`
  if (m > 0) return `${m}м ${s % 60}с`
  return `${s}с`
}

const highlightMap = ref<Record<string, { adminId: number; until: number }>>({})

const highlightMapPrice = ref<Record<string, { adminId: number; until: number }>>({})

function onAdminClick(data) {
  if (data.type === 'name') {
    const item = tableData.value.find(i => i.name === data.text)
    if (!item) return

    const duration = 2000 // 2 секунды

    highlightMap.value[item.id] = {
      adminId: data.adminId,
      until: Date.now() + duration
    }

    setTimeout(() => {
      delete highlightMap.value[item.id]
    }, duration)
  } else if (data.type === 'price') {
    const item = tableData.value.find(i => i.salePrice === data.text)
    if (!item) return

    const duration = 2000 // 2 секунды

    highlightMapPrice.value[item.id] = {
      adminId: data.adminId,
      until: Date.now() + duration
    }

    setTimeout(() => {
      delete highlightMapPrice.value[item.id]
    }, duration)
  }
}

watch(
  [
    () => wsStore.artefactProfitData,
    () => props.onlyGoodLots,
    () => props.liquidityWindow,
    () => props.minLiquiditySales,
    () => props.minBuyoutProfit
  ],
  () => {
    onArtefactProfitAll(wsStore.artefactProfitData)
  },
  { immediate: true }
)

onMounted(() => {
  wsStore.connect() // 👈 ВАЖНО

  wsStore.artefactSocket?.on('admin_click', onAdminClick)
})

onBeforeUnmount(() => {
  wsStore.artefactSocket?.off('admin_click', onAdminClick)
  tableData.value = []
})
</script>

<style lang="scss" scoped>
body {
  background-color: #1e1e1e;
  color: #f0e6d2;
  font-family: 'Arial', sans-serif;
  margin: 0;
  padding: 0;
}

.auction-table {
  width: 95%;
  margin: 20px auto;
  background-color: #2c2c2c;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
  padding: 20px;
}

.strategies-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 300px;
}

.strategy-card {
  background-color: #3c3c3c;
  border: 1px solid #555;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 4px;

  .strategy-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
    font-size: 13px;
    border-bottom: 1px solid #555;
    padding-bottom: 4px;
  }

  .strategy-details {
    font-size: 11px;
    line-height: 1.3;
    margin-bottom: 6px;

    div {
      margin-bottom: 2px;
      color: #e0e0e0;
    }
  }

  .lots-prices {
    font-size: 12px;
    color: #ccc;
    border-top: 1px solid #555;
    padding-top: 4px;
    word-break: break-all;
  }
}

.artifact-info {
  font-size: 12px;
  line-height: 1.4;
}

.row {
  display: flex;
  gap: 8px;
}

.key {
  min-width: 140px;
  color: #9aa0a6;
}

.value {
  word-break: break-word;
}

.row-highlight {
  animation: flash 0.6s ease-in-out 3;
  background-color: rgba(255, 255, 0, 0.25);
}

@keyframes flash {
  0% {
    background-color: rgba(255, 255, 0, 0.1);
  }
  50% {
    background-color: rgba(255, 200, 0, 0.5);
  }
  100% {
    background-color: rgba(255, 255, 0, 0.1);
  }
}

.click-indicator {
  position: absolute;
  left: 0;
  top: 20px;
  font-size: 11px;
  color: #ff9800;
}
</style>
